import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { Button } from 'components/buttons';
import { FormField } from 'components/form/formField/formField';
import { Input, InputType } from 'components/inputs';
import { Buttons } from 'components/modal/styles';

import { OrderItem, OrderItemType } from 'types/pwcOrder';

import { formatCurrencySymbol, formatNumberI18n } from 'utils/i18n';

import { CurrencyAdornment, Label } from '../styles';
import { CustomItemFormField, CustomItemFormValues, KEYS, LABELS } from './keys';
import { FormWrapper } from './styles';

interface CustomItemFormProps {
  fiatCurrencyCode: string;
  onAdd: (item: OrderItem) => void;
  onCancel: () => void;
}

const CustomItemForm = ({ fiatCurrencyCode, onAdd, onCancel }: CustomItemFormProps) => {
  const { t, i18n } = useTranslation();
  const form = useFormik<CustomItemFormValues>({
    initialValues: {
      [KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME]: '',
      [KEYS.CUSTOM_ITEM_FORM_FIELDS.FIAT_AMOUNT]: undefined,
    },
    validationSchema: Yup.object().shape({
      [KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME]: Yup.string().required(t(LABELS.ERRORS[KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME])),
      [KEYS.CUSTOM_ITEM_FORM_FIELDS.FIAT_AMOUNT]: Yup.number().required(t(LABELS.ERRORS[KEYS.CUSTOM_ITEM_FORM_FIELDS.FIAT_AMOUNT])),
    }),
    onSubmit: () => {
      const item: Pick<OrderItem, 'uuid' | 'name' | 'fiatAmount' | 'type'> = {
        uuid: `${KEYS.NEW_ITEM_PREFIX}${Date.now().toString()}`,
        name: form.values[KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME]!,
        fiatAmount: form.values[KEYS.CUSTOM_ITEM_FORM_FIELDS.FIAT_AMOUNT]!,
        type: OrderItemType.Custom,
      };

      onAdd(item as OrderItem);
      form.resetForm();
    },
  });
  const formattedSymbol = formatCurrencySymbol(i18n.language)(fiatCurrencyCode);
  const formattedPlaceholder = formatNumberI18n(i18n.language)(0);

  const onFormChange = (field: CustomItemFormField) => (value: string) => {
    form.setFieldTouched(field, true);
    form.setFieldValue(field, value);
  };

  const getFieldError = (field: CustomItemFormField): string | undefined => {
    if (!form.errors[field]) {
      return undefined;
    }

    if (!form.touched[field]) {
      return undefined;
    }

    return form.errors[field] as string | undefined;
  };

  const onAddItem = async () => {
    const errors = await form.validateForm();
    if (Object.keys(errors).length) {
      form.setErrors(errors);
      const touched = Object.keys(errors).reduce(
        (acc, key) => {
          acc[key as CustomItemFormField] = true;
          return acc;
        },
        {} as Record<CustomItemFormField, boolean>,
      );
      form.setTouched(touched);
      return;
    }

    form.submitForm();
  };

  return (
    <FormWrapper>
      <Label>{t(LABELS.FIELDS[KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME])}</Label>
      <FormField error={getFieldError(KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME)}>
        <Input
          placeholder={t(LABELS.PLACEHOLDERS[KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME])}
          value={form.values[KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME]}
          onChange={onFormChange(KEYS.CUSTOM_ITEM_FORM_FIELDS.NAME)}
        />
      </FormField>

      <Label>{t(LABELS.FIELDS[KEYS.CUSTOM_ITEM_FORM_FIELDS.FIAT_AMOUNT])}</Label>
      <FormField error={getFieldError(KEYS.CUSTOM_ITEM_FORM_FIELDS.FIAT_AMOUNT)}>
        <Input
          startAdornment={<CurrencyAdornment>{formattedSymbol}</CurrencyAdornment>}
          placeholder={formattedPlaceholder}
          type={InputType.Number}
          value={form.values[KEYS.CUSTOM_ITEM_FORM_FIELDS.FIAT_AMOUNT]?.toString()}
          onChange={onFormChange(KEYS.CUSTOM_ITEM_FORM_FIELDS.FIAT_AMOUNT)}
        />
      </FormField>
      <Buttons>
        <Button secondary onClick={onCancel}>
          {t(LABELS.ACTIONS.CANCEL)}
        </Button>
        <Button primary onClick={onAddItem} disabled={!form.isValid}>
          {t(LABELS.ACTIONS.ADD)}
        </Button>
      </Buttons>
    </FormWrapper>
  );
};

export default CustomItemForm;
